/* global VueRouter */
import { ref, computed, watch } from "vue"

// The notification store. The `<Notification>` component that renders this state lives in
// components/Notification.vue - it is imported by App.vue only, while the setters below are used
// all over the app, so they stay in this plain module.
const _state = ref({
  errors: [],
  warnings: [],
  loading: false,
  toast: null, // { message, type, id }
})

let _toastTimer = null
let _toastId = 0

const _processes = new Set()

const useNotification = () => {
  return {
    state: {
      errors: computed(() => _state.value.errors),
      warnings: computed(() => _state.value.warnings),
      loading: computed(() => _state.value.loading),
      toast: computed(() => _state.value.toast),
    },
  }
}

watch(
  () => _state.value.loading,
  (loading) => {
    if (loading) {
      document.body.classList.add("show-loader")
    } else {
      document.body.classList.remove("show-loader")
    }
  },
)

const clearNotifications = () => {
  _state.value.warnings.length = 0
  _state.value.errors.length = 0
}

// shown as a toast, so the page stays where the user tapped (callers may still pass a second "skipScroll" argument)
const setSuccess = (s) => {
  const message = typeof s === "string" ? s : s?.message
  if (!message) {
    setError(new Error("incorrect use of setSuccess"))
    return
  }
  // longer messages (e.g. a new order number) stay a bit longer
  showToast(message, "success", Math.min(3000 + message.length * 40, 8000))
}

const setWarning = (s) => {
  if (typeof s === "string") {
    _state.value.warnings.push({ message: s })
  } else if (s?.message) {
    _state.value.warnings.push(s)
  } else {
    _state.value.error = new Error("incorrect use of setWarning")
  }
  hideSuccessToast()
  window.scrollTo(0, 0)
}

const setError = (e) => {
  console.error(e)
  window.scrollTo(0, 0)
  if (e instanceof Error) {
    _state.value.errors.push(e)
  } else if (e?.message) {
    const error = new Error(e.message)
    if (e.stackTrace) {
      error.stackTrace = e.stackTrace
    }
    _state.value.errors.push(error)
  } else if (e) {
    _state.value.errors.push(new Error(typeof e === "string" ? e : JSON.stringify(e)))
  } else {
    _state.value.errors.push(new Error("Got null error."))
  }
  hideSuccessToast()
  _state.value.warnings.length = 0
}

// A short message at the bottom of the screen (where the thumb is on a phone) that doesn't scroll the page
// away from what the user just tapped, unlike setWarning/setError. type: a Bootstrap color (warning, danger, success).
const showToast = (message, type = "warning", durationMs = 7000) => {
  clearTimeout(_toastTimer)
  _state.value.toast = { message, type, id: ++_toastId }
  _toastTimer = setTimeout(hideToast, durationMs)
}

const hideSuccessToast = () => {
  if (_state.value.toast?.type === "success") {
    hideToast()
  }
}

const hideToast = () => {
  clearTimeout(_toastTimer)
  _state.value.toast = null
}

const startLoading = (subsystem) => {
  _state.value.errors.length = 0
  if (!subsystem?.length) {
    throw TypeError("subsystem must be a string")
  }
  _processes.add(subsystem)
  _state.value.loading = _processes.size > 0
  // console.log(`Start:${subsystem} ${_processes.size}`)
}

const stopLoading = (subsystem) => {
  if (!subsystem?.length) {
    throw TypeError("subsystem must be a string")
  }
  _processes.delete(subsystem)
  _state.value.loading = _processes.size > 0
  // console.log(`Stop:${subsystem} ${_processes.size}`)
}

export {
  useNotification,
  startLoading,
  stopLoading,
  setError,
  setWarning,
  setSuccess,
  clearNotifications,
  showToast,
  hideToast,
}
